import { useState } from "react";
import { motion } from "framer-motion";
import {
  CalendarCheck, Phone, Users, Briefcase, Search, ShieldCheck, Wallet,
  Check, X, ChevronDown,
} from "lucide-react";
import { SEO } from "@/components/site/SEO";
import { JsonLd, breadcrumbSchema, webPageSchema } from "@/components/site/Schema";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { TestimonialCard } from "@/components/site/Testimonials";
import { getTestimonials, STRATEGY_CALL_IDS } from "@/lib/testimonials";
import { CALENDLY_URL } from "@/lib/site";

const Section = ({ id, className = "bg-ivory", children }) => (
  <section id={id} className={`${className} py-20 md:py-28`}>
    <div className="container-lr">{children}</div>
  </section>
);

const Eyebrow = ({ children, light }) => (
  <div className="flex items-center gap-4">
    <span className="gold-rule" />
    <p className={`eyebrow ${light ? "text-gold" : ""}`}>{children}</p>
  </div>
);

const audiences = [
  { icon: Users, label: "Families seeking greater financial control" },
  { icon: Briefcase, label: "Business owners and entrepreneurs" },
  { icon: Search, label: "People researching Infinite Banking" },
  { icon: ShieldCheck, label: "People reviewing retirement or long-term care needs" },
  { icon: Wallet, label: "People who want clearer cash-flow and debt-payoff education" },
];

const callOne = ["Current financial situation", "Financial priorities", "Experience with Infinite Banking", "Current level of financial education", "Initial questions", "Whether a deeper conversation makes sense"];
const callTwo = ["What you’re trying to accomplish", "Potential Infinite Banking applications", "Cash-flow and funding considerations", "Broader protection and retirement priorities", "Questions about process and implementation", "Appropriate next educational steps"];
const expect = ["A phone conversation", "Clear education", "Questions about goals and priorities", "Time to ask questions", "No obligation to move forward", "No pressure to make an immediate decision"];
const isNot = ["Not a high-pressure sales call", "Not a promise that Infinite Banking is right for every person", "Not individualized tax or legal advice", "Not a guarantee of financial performance", "Not a substitute for qualified legal, tax, or investment professionals"];

const faqs = [
  { q: "Is the first call free?", a: "Yes. The first call is a free, no-obligation conversation." },
  { q: "How long is the call?", a: "The first call is a general 45-minute phone conversation. A second, deeper 45-minute education and strategy phone conversation can follow if it makes sense." },
  { q: "Is the call by phone or video?", a: "Both the first and second calls are phone conversations." },
  { q: "Do I need to understand Infinite Banking first?", a: "No. The first call is a general introduction, and education comes before any implementation. You can come with questions at any level." },
  { q: "Is there any obligation to move forward?", a: "No. There is no obligation and no pressure to make an immediate decision." },
  { q: "What happens after the first call?", a: "If a deeper conversation makes sense, you can schedule a second 45-minute education and strategy phone conversation to explore goals and appropriate next steps." },
  { q: "Should my spouse or business partner attend?", a: "You’re welcome to include a spouse or business partner if they’re part of the financial decisions you want to discuss." },
  { q: "What should I prepare before the call?", a: "Nothing formal is required. It helps to think about your current situation, priorities, and any initial questions you’d like to discuss." },
];

const FaqItem = ({ q, a, i }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-taupe/50" data-testid={`faq-${i}`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="w-full flex items-center justify-between gap-4 py-5 text-left focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold"
      >
        <span className="font-serif text-lg md:text-xl text-legacy">{q}</span>
        <ChevronDown size={20} className={`shrink-0 text-bronze transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <p className="pb-6 -mt-1 text-slate text-[0.95rem] leading-relaxed max-w-3xl">{a}</p>}
    </div>
  );
};

const CallCard = ({ tag, title, items, footnote }) => (
  <div className="lr-card p-8 md:p-10">
    <span className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-legacy text-gold"><Phone size={20} strokeWidth={1.5} /></span>
    <p className="eyebrow mt-7 text-bronze">{tag}</p>
    <h3 className="mt-2 font-serif text-2xl text-legacy leading-tight">{title}</h3>
    <p className="mt-2 text-[0.8rem] font-semibold tracking-wide text-slate/80 uppercase">45 minutes • Phone</p>
    <span className="gold-rule mt-6 mb-5" />
    <ul className="space-y-2.5">
      {items.map((it) => (
        <li key={it} className="flex gap-3 text-slate text-[0.93rem] leading-relaxed"><Check size={17} className="text-gold shrink-0 mt-1" />{it}</li>
      ))}
    </ul>
    {footnote && <p className="mt-5 text-[0.78rem] text-slate/70 leading-relaxed">{footnote}</p>}
  </div>
);

export default function StrategyCall() {
  const [iframeLoaded, setIframeLoaded] = useState(false);
  const testimonials = getTestimonials(STRATEGY_CALL_IDS);

  return (
    <div className="min-h-screen bg-ivory" data-testid="strategy-call-page">
      <SEO
        title="Book a Free Strategy Call | Legacy Life & Retirement"
        description="Book a free 45-minute introductory phone call with Shawn King to discuss Infinite Banking, cash flow, retirement planning, and your financial priorities."
      />
      <JsonLd id="ld-sc-breadcrumb" data={breadcrumbSchema([{ name: "Home", path: "/" }, { name: "Book Your Free Strategy Call", path: "/strategy-call" }])} />
      <JsonLd id="ld-sc-webpage" data={webPageSchema({ path: "/strategy-call", name: "Book Your Free Strategy Call" })} />
      <Header />
      <main>
        {/* Hero */}
        <section data-testid="hero-section" className="relative bg-ivory pt-[120px] md:pt-[150px] pb-20 md:pb-24 overflow-hidden">
          <div className="pointer-events-none absolute inset-0 grid-lines opacity-50" />
          <div className="container-lr relative max-w-3xl">
            <Eyebrow>Education First</Eyebrow>
            <h1 className="mt-6 font-serif font-semibold tracking-display text-legacy text-4xl sm:text-5xl lg:text-6xl leading-[1.05]">
              Start With a Conversation, Not a Commitment.
            </h1>
            <p className="mt-7 text-slate text-base md:text-lg leading-relaxed">
              The first step is not choosing a product. It is understanding where you are, what you are trying to accomplish, and whether the financial concepts we teach align with your priorities.
            </p>
            <p className="mt-4 text-slate/90 text-sm md:text-base leading-relaxed">
              Legacy Life &amp; Retirement begins with education so you can make a more informed decision without pressure or obligation.
            </p>
            <a href="#booking" data-testid="hero-book-cta" className="btn-base btn-primary mt-9"><CalendarCheck size={18} />Book Your Free Strategy Call</a>
          </div>
        </section>

        {/* Who the call is for */}
        <Section id="who" className="bg-[#EFE8D9]">
          <div className="max-w-2xl"><Eyebrow>Who the Call Is For</Eyebrow>
            <h2 className="mt-6 font-serif font-semibold tracking-display text-legacy text-3xl md:text-4xl leading-[1.1]">A good fit if you’re exploring any of these.</h2>
          </div>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {audiences.map(({ icon: Icon, label }, i) => (
              <motion.div key={label} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.08 }} className="lr-card p-7 flex items-start gap-4">
                <span className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-legacy text-gold"><Icon size={20} strokeWidth={1.5} /></span>
                <p className="text-legacy/90 text-[0.95rem] leading-relaxed pt-1">{label}</p>
              </motion.div>
            ))}
          </div>
        </Section>

        {/* Two-call process */}
        <Section id="process">
          <div className="max-w-2xl"><Eyebrow>The Process</Eyebrow>
            <h2 className="mt-6 font-serif font-semibold tracking-display text-legacy text-3xl md:text-4xl leading-[1.1]">Two focused conversations.</h2>
          </div>
          <div className="mt-12 grid md:grid-cols-2 gap-6">
            <CallCard tag="Call One" title="General Introductory Call" items={callOne} />
            <CallCard tag="Call Two" title="Deeper Education and Strategy Conversation" items={callTwo} footnote="This second call is an education and strategy conversation. It is not guaranteed individualized financial advice." />
          </div>
        </Section>

        {/* What to expect + What this call is not */}
        <Section id="expectations" className="bg-[#EFE8D9]">
          <div className="grid md:grid-cols-2 gap-6">
            <div className="lr-card p-8 md:p-10">
              <Eyebrow>What to Expect</Eyebrow>
              <ul className="mt-7 space-y-3">
                {expect.map((it) => <li key={it} className="flex gap-3 text-slate text-[0.95rem] leading-relaxed"><Check size={18} className="text-gold shrink-0 mt-0.5" />{it}</li>)}
              </ul>
            </div>
            <div className="lr-card p-8 md:p-10">
              <Eyebrow>What This Call Is Not</Eyebrow>
              <ul className="mt-7 space-y-3">
                {isNot.map((it) => <li key={it} className="flex gap-3 text-slate text-[0.95rem] leading-relaxed"><X size={18} className="text-bronze shrink-0 mt-0.5" />{it}</li>)}
              </ul>
            </div>
          </div>
        </Section>

        {/* Testimonials */}
        <Section id="experiences">
          <div className="max-w-2xl"><Eyebrow>Client Experiences</Eyebrow>
            <h2 className="mt-6 font-serif font-semibold tracking-display text-legacy text-3xl md:text-4xl leading-[1.1]">Education, patience, and clear guidance.</h2>
          </div>
          <div className="mt-12 grid md:grid-cols-3 gap-6 items-start">
            {testimonials.map((t, i) => <TestimonialCard key={t.id} t={t} index={i} />)}
          </div>
        </Section>

        {/* Calendly booking */}
        <Section id="booking" className="bg-legacy text-ivory">
          <div className="max-w-2xl"><Eyebrow light>Book Your Call</Eyebrow>
            <h2 className="mt-6 font-serif font-semibold tracking-display text-ivory text-3xl md:text-4xl leading-[1.1]">Choose a time that works for you.</h2>
            <p className="mt-5 text-taupe text-base leading-relaxed">Pick a slot below. If the scheduler doesn’t load, use the button to open it in a new tab.</p>
          </div>
          <div className="mt-10 rounded-card overflow-hidden border border-gold/30 bg-ivory relative" style={{ minHeight: 680 }}>
            {!iframeLoaded && (
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 text-slate" data-testid="calendly-loading">
                <span className="h-10 w-10 rounded-full border-2 border-taupe/40 border-t-gold animate-spin" />
                <p className="text-sm">Loading the scheduler…</p>
                <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer" className="btn-base btn-primary text-sm">Open Scheduler in New Tab</a>
              </div>
            )}
            <iframe
              src={CALENDLY_URL}
              title="Book a strategy call with Shawn King"
              loading="lazy"
              onLoad={() => setIframeLoaded(true)}
              data-testid="calendly-iframe"
              className="w-full"
              style={{ height: 680, border: "none" }}
            />
          </div>
          <div className="mt-6 text-center">
            <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer" data-testid="calendly-fallback" className="btn-base btn-gold"><CalendarCheck size={18} />Book Your Free Strategy Call</a>
          </div>
        </Section>

        {/* FAQs */}
        <Section id="faqs">
          <div className="max-w-2xl"><Eyebrow>Frequently Asked Questions</Eyebrow>
            <h2 className="mt-6 font-serif font-semibold tracking-display text-legacy text-3xl md:text-4xl leading-[1.1]">Good to know before you book.</h2>
          </div>
          <div className="mt-10 max-w-3xl">
            {faqs.map((f, i) => <FaqItem key={i} q={f.q} a={f.a} i={i} />)}
          </div>
        </Section>

        {/* Final reassurance */}
        <Section id="reassurance" className="bg-[#EFE8D9]">
          <div className="max-w-3xl mx-auto text-center">
            <span className="gold-rule mx-auto" />
            <h2 className="mt-6 font-serif font-semibold tracking-display text-legacy text-3xl md:text-4xl leading-[1.1]">No pressure. Just a clear, honest conversation.</h2>
            <p className="mt-5 text-slate text-base md:text-lg leading-relaxed">You’re simply starting with education. Whether or not Infinite Banking is right for you, you’ll leave the call with a clearer understanding of your options.</p>
            <a href="#booking" data-testid="reassurance-cta" className="btn-base btn-primary mt-8"><CalendarCheck size={18} />Book Your Free Strategy Call</a>
          </div>
        </Section>
      </main>
      <Footer />
    </div>
  );
}
